
import React, { useState, useEffect } from "react"; 
import { useNavigate, useLocation, useParams } from "react-router-dom"; 
import { useAuth } from "../../contexts/AuthContext"; 
import { useToast } from "../../contexts/ToastContext"; 
import leaveService from "../../services/leaveService"; 
import { getErrorMessage } from "../../utils/errorHandler"; 
import FormCard from "../../components/ui/FormCard"; 
import FormSection from "../../components/ui/FormSection"; 
import FormActions from "../../components/ui/FormActions"; 
import Breadcrumb from "../../components/ui/Breadcrumb"; 
import "../../styles/FormCard.css"; 
 
const CalendarEventNew = () => { 
  const { user } = useAuth(); 
  const navigate = useNavigate(); 
  const location = useLocation(); 
  const toast = useToast(); 
 
  const { id } = useParams(); 
  const isEditMode = Boolean(id); 
  const [submitting, setSubmitting] = useState(false); 
   
  // Get the 'from' location from navigation state, default to /rh/calendar 
  const fromLocation = location.state?.from || "/rh/calendar"; 
  const prefillDate = location.state?.prefillDate; 
 
  const [formData, setFormData] = useState({ 
    title: "", 
    event_type: "PAID_HOLIDAY", 
    start_date: "", 
    end_date: "", 
    start_time: "", 
    end_time: "", 
    description: "", 
    location: "", 
    color: "#3b82f6", 
  }); 
  const [errors, setErrors] = useState({}); 
 
  // Prefill date if provided
  useEffect(() => { 
    if (!isEditMode && prefillDate) { 
      setFormData(prev => ({ 
        ...prev, 
        start_date: prefillDate, 
        end_date: prefillDate 
      })); 
    } 
  }, [prefillDate, isEditMode]); 
 
  const handleChange = (e) => { 
    const { name, value } = e.target; 
    setFormData(prev => ({ ...prev, [name]: value })); 
    if (errors[name]) { 
      setErrors(prev => ({ ...prev, [name]: "" })); 
    } 
  }; 
 
  const validate = () => { 
    const newErrors = {}; 
    if (!formData.title.trim()) {
      newErrors.title = "Le titre est requis";
    }

    if (!formData.start_date) {
      newErrors.start_date = "La date de début est requise";
    }

    if (!formData.end_date) {
      newErrors.end_date = "La date de fin est requise";
    }
     
    setErrors(newErrors); 
    return Object.keys(newErrors).length === 0; 
  }; 
 
  const handleSubmit = async (e) => { 
    e.preventDefault(); 
    if (!validate()) return; 
 
    try { 
      setSubmitting(true); 

      /*
       * ON GARDE TOUS LES ATTRIBUTS DU FORMULAIRE.
       *
       * Mais au lieu d'envoyer vers calendar_events,
       * on envoie les données nécessaires à holidays.
       */

     const holidayData = {
        name: formData.title,
        date: formData.start_date,
        holiday_type:
          formData.event_type === "PAID_HOLIDAY"
            ? "NATIONAL"
            : "INTERNE_ENTREPRISE",
        description: formData.description || null,
        year: formData.start_date
          ? new Date(formData.start_date).getFullYear()
          : null,
        duration_days: 1,
        is_active: true,
        is_religious: false,
        is_recurring: false,

        // Couleur sélectionnée
        color: formData.color,
      };
      console.log(
        "[CalendarEventNew] Création du jour férié :",
        holidayData
      );

      if (isEditMode) {
        await leaveService.updateHoliday(id, holidayData);
        toast.success("Jour férié modifié avec succès");
      } else {
        await leaveService.createHoliday(holidayData);
        toast.success("Jour férié ajouté avec succès");
      }

      navigate(fromLocation); 
    } catch (err) { 
      console.error("Error creating holiday:", err); 
      toast.error(getErrorMessage(err)); 
    } finally { 
      setSubmitting(false); 
    } 
  }; 
 
  const handleCancel = () => { 
    navigate(fromLocation); 
  }; 
 
  const breadcrumbItems = [ 
    { label: "RH", to: "/rh/dashboard" }, 
    { label: "Calendrier", to: "/rh/calendar" }, 
    { label: isEditMode ? "Modifier un jour férié" : "Ajouter un jour férié" } 
  ]; 
 
  return ( 
    <div style={{ padding: "24px" }}> 
      <Breadcrumb items={breadcrumbItems} /> 
      <FormCard 
        title={isEditMode ? "Modifier un jour férié" : "Ajouter un jour férié"} 
        showBackButton 
        onBack={handleCancel}
      > 
        <form onSubmit={handleSubmit}> 

          <FormSection title="Informations du jour férié"> 
            <div className="form-group"> 
              <label>Titre *</label> 
              <input 
                type="text" 
                name="title" 
                value={formData.title} 
                onChange={handleChange} 
                className={errors.title ? "error" : ""} 
              /> 
              {errors.title && (
                <span className="error">{errors.title}</span>
              )} 
            </div> 

            <div className="form-group"> 
              <label>Type de jour férié *</label> 
              <select 
                name="event_type" 
                value={formData.event_type} 
                onChange={handleChange} 
              > 
                <option value="PAID_HOLIDAY">Chômé payé</option> 
                <option value="UNPAID_HOLIDAY">Chômé non payé</option> 
              </select> 
            </div> 

            <div className="form-group"> 
              <label>Description</label> 
              <textarea 
                name="description" 
                value={formData.description} 
                onChange={handleChange} 
                placeholder="Description du jour férié..." 
              /> 
            </div> 

            <div className="form-group"> 
              <label>Localisation</label> 
              <input 
                type="text" 
                name="location" 
                value={formData.location} 
                onChange={handleChange} 
                placeholder="Salle, lieu, etc." 
              /> 
            </div> 
          </FormSection> 
 
          <FormSection title="Date et heure"> 
            <div className="form-group"> 
              <label>Date de début *</label> 
              <input 
                type="date" 
                name="start_date" 
                value={formData.start_date} 
                onChange={handleChange} 
                className={errors.start_date ? "error" : ""} 
              /> 
              {errors.start_date && (
                <span className="error">{errors.start_date}</span>
              )} 
            </div> 

            <div className="form-group"> 
              <label>Heure de début</label> 
              <input 
                type="time" 
                name="start_time" 
                value={formData.start_time} 
                onChange={handleChange} 
              /> 
            </div> 

            <div className="form-group"> 
              <label>Date de fin *</label> 
              <input 
                type="date" 
                name="end_date" 
                value={formData.end_date} 
                onChange={handleChange} 
                className={errors.end_date ? "error" : ""} 
              /> 
              {errors.end_date && (
                <span className="error">{errors.end_date}</span>
              )} 
            </div> 

            <div className="form-group"> 
              <label>Heure de fin</label> 
              <input 
                type="time" 
                name="end_time" 
                value={formData.end_time} 
                onChange={handleChange} 
              /> 
            </div> 
          </FormSection> 
 
          <FormSection title="Affichage"> 
            <div className="form-group"> 
              <label>Couleur</label> 
              <input 
                type="color" 
                name="color" 
                value={formData.color} 
                onChange={handleChange} 
                style={{ width: "100px", height: "40px" }} 
              /> 
            </div> 
          </FormSection> 
 
          <FormActions 
            onCancel={handleCancel} 
            onSave={handleSubmit} 
            saveText={ 
              isEditMode 
                ? "Enregistrer les modifications" 
                : "Ajouter le jour férié" 
            } 
            isLoading={submitting} 
          /> 
 
        </form> 
      </FormCard> 
    </div> 
  ); 
}; 
 
export default CalendarEventNew;

